'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { AddSdgsDialogProps } from '@/types';
import { SDG_LABELS, sdgIconSrc } from '@/types';
import { Plus, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function AddSdgsDialog({
  onChange,
  onChangeMultiple,
  existingSDGs = [],
}: AddSdgsDialogProps & {
  existingSDGs?: number[];
  onChangeMultiple?: (sdgs: number[]) => Promise<void> | void;
}) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<number[]>([]);
  const [isAdding, setIsAdding] = React.useState(false);
  const [_, setAddingIndex] = React.useState<number>(-1);

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelected([]);
      setAddingIndex(-1);
    }
  }, [open]);

  const toggleSDG = (goal: number) => {
    // Don't allow selecting already existing SDGs
    if (existingSDGs.includes(goal)) return;

    setSelected((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) return;

    setIsAdding(true);

    // If onChangeMultiple is provided, use it (sends all at once)
    if (onChangeMultiple) {
      await onChangeMultiple(selected);
      setIsAdding(false);
      setSelected([]);
      setOpen(false);
      return;
    }

    // Otherwise, add SDGs one by one with cumulative array
    // We need to build up the array as we go
    let currentSdgs = [...existingSDGs];
    for (let i = 0; i < selected.length; i++) {
      const sdg = selected[i];
      setAddingIndex(i);
      currentSdgs = [...currentSdgs, sdg];
      // Pass the SDG to add - the parent should handle merging
      await onChange?.(sdg);
      // Small delay to allow state to propagate
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setIsAdding(false);
    setAddingIndex(-1);
    setSelected([]);
    setOpen(false);
  };

  const removeFromSelection = (goal: number) => {
    setSelected((prev) => prev.filter((g) => g !== goal));
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="gap-2">
        <Plus className="h-4 w-4" />
        Add SDGs
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-muted/30">
            <DialogTitle className="text-xl">
              Select Sustainable Development Goals
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Choose one or more SDGs to add to this project. Already added SDGs
              are shown as disabled.
            </DialogDescription>
          </DialogHeader>

          {/* Selected SDGs Preview */}
          {selected.length > 0 && (
            <div className="px-6 py-3 border-b bg-primary/5 max-h-30 overflow-auto">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Selected ({selected.length}):
                </span>
                {selected.map((goal) => (
                  <Badge
                    key={goal}
                    variant="secondary"
                    className="rounded-full pl-1.5 pr-1 py-0.5 gap-1.5 cursor-pointer hover:bg-destructive/10 transition-colors"
                    onClick={() => removeFromSelection(goal)}
                  >
                    <img
                      src={sdgIconSrc(goal) || '/placeholder.svg'}
                      alt={`SDG ${goal}`}
                      className="h-4 w-4"
                    />
                    <span className="text-xs font-medium">
                      SDG {SDG_LABELS[Number(goal)]}
                    </span>
                    <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* SDG Grid */}
          <ScrollArea className="h-[400px]">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Array.from({ length: 17 }, (_, i) => i + 1).map((goal) => {
                const isExisting = existingSDGs.includes(goal);
                const isSelected = selected.includes(goal);

                return (
                  <button
                    key={goal}
                    type="button"
                    disabled={isExisting}
                    onClick={() => toggleSDG(goal)}
                    className={cn(
                      'relative group flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 border-none',
                      isExisting &&
                        'cursor-not-allowed',
                      isSelected &&
                        !isExisting &&
                        '',
                      !isSelected &&
                        !isExisting &&
                        'cursor-pointer'
                    )}
                  >
                    {/* Selection indicator */}
                    {isSelected && !isExisting && (
                      <div className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary flex items-center justify-center shadow-md">
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}

                    {/* Already added indicator */}
                    {isExisting && (
                      <div className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-muted-foreground/50 flex items-center justify-center">
                        <Check className="h-3 w-3 text-background" />
                      </div>
                    )}

                    <img
                      src={sdgIconSrc(goal) || '/placeholder.svg'}
                      alt={`SDG ${goal}`}
                      className={cn(
                        'h-25 w-30 shadow-sm transition-transform duration-200'
                        // !isExisting && 'group-hover:scale-105',
                        // isExisting && 'grayscale'
                      )}
                    />
                    <div className="text-center">
                      <span
                        className={cn(
                          'text-[10px] text-muted-foreground line-clamp-2 leading-tight mt-0.5'
                          // isExisting && 'text-muted-foreground/60'
                        )}
                      >
                        {SDG_LABELS[goal]}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              {existingSDGs.length > 0 && (
                <span>{existingSDGs.length} SDG(s) already added</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={isAdding}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={selected.length === 0 || isAdding}
                className="min-w-[100px]"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Adding...
                  </>
                ) : (
                  <>Add {selected.length > 0 && `(${selected.length})`}</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
